import React, { useRef, useMemo, useState, useEffect, lazy, Suspense } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useThrottledFrame } from '../hooks/useThrottledFrame';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { useWorldStore, AtmosphereSettings } from '../stores/worldStore';
import { useLiteStore } from '../stores/liteStore';

// Lazy-load postprocessing to avoid bundle cost when bloom is off
const EffectComposer = lazy(() => import('@react-three/postprocessing').then(m => ({ default: m.EffectComposer })));
const Bloom = lazy(() => import('@react-three/postprocessing').then(m => ({ default: m.Bloom })));

// ---- Procedural soft circle texture (generated once, shared) ----
const _softTextureCache: Record<string, THREE.Texture> = {};
function getSoftParticleTexture(style: 'soft' | 'streak' | 'glow' | 'bubble'): THREE.Texture {
  if (_softTextureCache[style]) return _softTextureCache[style];

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const half = size / 2;

  if (style === 'soft') {
    // Soft radial gradient circle — works for snow, dust, embers
    const grad = ctx.createRadialGradient(half, half, 0, half, half, half);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.6)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  } else if (style === 'streak') {
    // Vertical streak — for rain
    const grad = ctx.createLinearGradient(half, 0, half, size);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.3, 'rgba(255,255,255,0.8)');
    grad.addColorStop(0.7, 'rgba(255,255,255,0.8)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(half - 1, 0, 2, size);
  } else if (style === 'bubble') {
    // Ring — bright rim, faint fill, small highlight — reads as a bubble
    const grad = ctx.createRadialGradient(half, half, 0, half, half, half);
    grad.addColorStop(0, 'rgba(255,255,255,0.08)');
    grad.addColorStop(0.72, 'rgba(255,255,255,0.12)');
    grad.addColorStop(0.85, 'rgba(255,255,255,0.9)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    // Specular highlight, upper-left — the detail that sells the sphere
    const hl = ctx.createRadialGradient(half * 0.62, half * 0.62, 0, half * 0.62, half * 0.62, size * 0.14);
    hl.addColorStop(0, 'rgba(255,255,255,0.95)');
    hl.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hl;
    ctx.fillRect(0, 0, size, size);
  } else {
    // Glow — bright core, wide falloff — for fireflies
    const grad = ctx.createRadialGradient(half, half, 0, half, half, half);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.15, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.5, 'rgba(255,255,255,0.3)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  _softTextureCache[style] = tex;
  return tex;
}

// ---- Fog Manager (sets scene.fog imperatively) ----
const FogManager: React.FC<{ fog: AtmosphereSettings['fog']; color: string; near: number; far: number; density: number }> = ({ fog, color, near, far, density }) => {
  const { scene } = useThree();

  useThrottledFrame(() => {
    if (fog === 'none') {
      if (scene.fog) scene.fog = null;
      return;
    }
    const c = new THREE.Color(color);
    if (fog === 'linear') {
      if (!(scene.fog instanceof THREE.Fog) || (scene.fog as THREE.Fog).near !== near || (scene.fog as THREE.Fog).far !== far) {
        scene.fog = new THREE.Fog(c, near, far);
      } else {
        scene.fog.color.set(c);
      }
    } else if (fog === 'exponential') {
      if (!(scene.fog instanceof THREE.FogExp2) || (scene.fog as THREE.FogExp2).density !== density) {
        scene.fog = new THREE.FogExp2(c, density);
      } else {
        scene.fog.color.set(c);
      }
    }
  });

  return null;
};

// ---- Animated Fog Planes (Roblox-style: scrolling transparent planes for heavy fog) ----
const FogPlanes: React.FC<{ color: string; opacity: number }> = ({ color, opacity }) => {
  const planesRef = useRef<THREE.Group>(null);
  const { camera } = useThree();

  // Pre-allocated vector to avoid GC
  const _camPos = useMemo(() => new THREE.Vector3(), []);

  useThrottledFrame((state) => {
    if (!planesRef.current) return;
    // Follow camera XZ, sit at fixed Y layers
    _camPos.copy(camera.position);
    planesRef.current.position.x = _camPos.x;
    planesRef.current.position.z = _camPos.z;

    // Slowly scroll the planes for a drifting fog look
    const t = state.clock.elapsedTime;
    const children = planesRef.current.children;
    for (let i = 0; i < children.length; i++) {
      const mesh = children[i] as THREE.Mesh;
      mesh.position.x = Math.sin(t * 0.1 + i * 2.1) * 3;
      mesh.position.z = Math.cos(t * 0.08 + i * 1.7) * 3;
      mesh.rotation.z = t * 0.02 * (i % 2 === 0 ? 1 : -1);
    }
  });

  return (
    <group ref={planesRef}>
      {[1, 3, 5].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[60, 60]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={opacity * (1 - i * 0.25)}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
};

// ---- Particle System (Roblox-inspired: soft textures, distance culling, camera-relative) ----

// Hard budget: cap particles for VR perf
const MAX_PARTICLES_VR = 1000;
const MAX_PARTICLES_DESKTOP = 2000;
const CULL_DISTANCE_SQ = 40 * 40; // Don't update particles beyond 40 units from camera

const PARTICLE_CONFIGS = {
  rain:      { speed: 8,   spread: 30, height: 20, size: 0.12, opacity: 0.5, gravity: true,  texture: 'streak' as const, blending: THREE.NormalBlending },
  snow:      { speed: 1.5, spread: 30, height: 20, size: 0.25, opacity: 0.75, gravity: true,  texture: 'soft' as const,   blending: THREE.NormalBlending },
  dust:      { speed: 0.3, spread: 25, height: 8,  size: 0.15, opacity: 0.35, gravity: false, texture: 'soft' as const,   blending: THREE.NormalBlending },
  fireflies: { speed: 0.5, spread: 18, height: 6,  size: 0.3,  opacity: 0.9,  gravity: false, texture: 'glow' as const,   blending: THREE.AdditiveBlending },
  embers:    { speed: 1.0, spread: 12, height: 10, size: 0.12, opacity: 0.7,  gravity: false, texture: 'glow' as const,   blending: THREE.AdditiveBlending },
  bubbles:   { speed: 0.9, spread: 20, height: 14, size: 0.2,  opacity: 0.55, gravity: false, texture: 'bubble' as const, blending: THREE.NormalBlending },
};

const ParticleSystem: React.FC<{ type: keyof typeof PARTICLE_CONFIGS; count: number; color: string }> = ({ type, count, color }) => {
  const pointsRef = useRef<THREE.Points>(null);
  const { camera } = useThree();
  const config = PARTICLE_CONFIGS[type];
  const isPresenting = useXR((s) => !!s.session);

  // Clamp to hard budget — use actual VR session state
  const budget = isPresenting ? MAX_PARTICLES_VR : MAX_PARTICLES_DESKTOP;
  const actualCount = Math.min(count, budget);

  const texture = useMemo(() => getSoftParticleTexture(config.texture), [config.texture]);

  const [positions, velocities, sizes] = useMemo(() => {
    const pos = new Float32Array(actualCount * 3);
    const vel = new Float32Array(actualCount * 3);
    const sz = new Float32Array(actualCount);
    for (let i = 0; i < actualCount; i++) {
      const i3 = i * 3;
      pos[i3] = (Math.random() - 0.5) * config.spread;
      pos[i3 + 1] = Math.random() * config.height;
      pos[i3 + 2] = (Math.random() - 0.5) * config.spread;

      // Size variation (±30%) for natural look
      sz[i] = config.size * (0.7 + Math.random() * 0.6);

      if (type === 'rain') {
        vel[i3] = (Math.random() - 0.5) * 0.3; // slight wind drift
        vel[i3 + 1] = -config.speed - Math.random() * 2;
        vel[i3 + 2] = (Math.random() - 0.5) * 0.3;
      } else if (type === 'snow') {
        vel[i3] = (Math.random() - 0.5) * 0.5;
        vel[i3 + 1] = -config.speed - Math.random() * 0.5;
        vel[i3 + 2] = (Math.random() - 0.5) * 0.5;
      } else if (type === 'fireflies') {
        vel[i3] = (Math.random() - 0.5) * config.speed;
        vel[i3 + 1] = (Math.random() - 0.5) * config.speed * 0.5;
        vel[i3 + 2] = (Math.random() - 0.5) * config.speed;
      } else if (type === 'embers') {
        vel[i3] = (Math.random() - 0.5) * config.speed * 0.3;
        vel[i3 + 1] = config.speed * (0.5 + Math.random() * 0.5);
        vel[i3 + 2] = (Math.random() - 0.5) * config.speed * 0.3;
      } else if (type === 'bubbles') {
        // Rise only — lateral motion comes from per-frame wobble, and giving
        // bubbles a stored drift too made them slide diagonally like snow
        // played backwards. Varied rise speed doubles as depth cueing.
        vel[i3] = 0;
        vel[i3 + 1] = config.speed * (0.4 + Math.random() * 0.9);
        vel[i3 + 2] = 0;
      } else {
        // dust
        vel[i3] = (Math.random() - 0.5) * config.speed;
        vel[i3 + 1] = (Math.random() - 0.5) * config.speed * 0.3;
        vel[i3 + 2] = (Math.random() - 0.5) * config.speed;
      }
    }
    return [pos, vel, sz];
  }, [actualCount, type, config.spread, config.height, config.speed, config.size]);

  // Pre-allocate camera position vector
  const _camWorld = useMemo(() => new THREE.Vector3(), []);

  useFrame((_, delta) => {
    if (!pointsRef.current) return;
    const posArr = pointsRef.current.geometry.attributes.position.array as Float32Array;

    // Get camera world position for distance culling + camera-relative spawning
    camera.getWorldPosition(_camWorld);
    const camX = _camWorld.x;
    const camZ = _camWorld.z;

    for (let i = 0; i < actualCount; i++) {
      const i3 = i * 3;

      // Distance culling: skip particles far from camera
      const dx = posArr[i3] - camX;
      const dz = posArr[i3 + 2] - camZ;
      const distSq = dx * dx + dz * dz;

      if (distSq > CULL_DISTANCE_SQ) {
        // Respawn near camera instead of updating far-away particles
        posArr[i3] = camX + (Math.random() - 0.5) * config.spread;
        posArr[i3 + 1] = Math.random() * config.height;
        posArr[i3 + 2] = camZ + (Math.random() - 0.5) * config.spread;
        continue;
      }

      if (type === 'fireflies') {
        // Organic wander motion with per-particle phase
        const t = Date.now() * 0.001;
        posArr[i3] += Math.sin(t + i * 0.73) * delta * 0.5;
        posArr[i3 + 1] += Math.cos(t * 1.3 + i * 1.1) * delta * 0.3;
        posArr[i3 + 2] += Math.sin(t * 0.7 + i * 1.9) * delta * 0.5;
      } else if (type === 'bubbles') {
        // Rise with sinusoidal side-to-side wobble, per-particle phase
        const t = Date.now() * 0.001;
        posArr[i3] += Math.sin(t * 1.6 + i * 0.91) * delta * 0.35;
        posArr[i3 + 1] += velocities[i3 + 1] * delta;
        posArr[i3 + 2] += Math.cos(t * 1.3 + i * 1.37) * delta * 0.35;
      } else {
        posArr[i3] += velocities[i3] * delta;
        posArr[i3 + 1] += velocities[i3 + 1] * delta;
        posArr[i3 + 2] += velocities[i3 + 2] * delta;
      }

      // Respawn out-of-bounds particles near camera (camera-relative spawning)
      if (config.gravity && posArr[i3 + 1] < -0.5) {
        posArr[i3] = camX + (Math.random() - 0.5) * config.spread;
        posArr[i3 + 1] = config.height;
        posArr[i3 + 2] = camZ + (Math.random() - 0.5) * config.spread;
      } else if (!config.gravity) {
        if (posArr[i3 + 1] > config.height) posArr[i3 + 1] = 0;
        if (posArr[i3 + 1] < -0.5) posArr[i3 + 1] = config.height;
        const half = config.spread / 2;
        if (Math.abs(posArr[i3] - camX) > half) {
          posArr[i3] = camX + (Math.random() - 0.5) * config.spread;
        }
        if (Math.abs(posArr[i3 + 2] - camZ) > half) {
          posArr[i3 + 2] = camZ + (Math.random() - 0.5) * config.spread;
        }
      }
    }

    pointsRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={pointsRef} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
          count={actualCount}
        />
        <bufferAttribute
          attach="attributes-size"
          args={[sizes, 1]}
          count={actualCount}
        />
      </bufferGeometry>
      <pointsMaterial
        map={texture}
        color={color}
        size={config.size}
        sizeAttenuation
        transparent
        opacity={config.opacity}
        depthWrite={false}
        blending={config.blending}
        alphaTest={0.01}
      />
    </points>
  );
};

// ---- Area particles: embers / fireflies held inside a box (a dance floor) ----
// All motion is in the vertex shader (rise, sway, flicker, fade at the ends),
// so the CPU does nothing per frame: one draw call, no buffer uploads.
const AREA_VERT = `
  uniform float uTime, uSize, uHalfH, uRise;
  uniform vec3 uMin, uMax;
  attribute vec4 aSeed;
  varying float vAlpha;
  void main() {
    vec3 span = uMax - uMin;
    float life = fract(aSeed.w + uTime * uRise / span.y * (0.6 + aSeed.z * 0.8));
    vec3 p = uMin + vec3(aSeed.x * span.x, life * span.y, aSeed.y * span.z);
    p.x += sin(uTime * (0.7 + aSeed.z) + aSeed.w * 30.0) * 0.18;
    p.z += cos(uTime * (0.5 + aSeed.x) + aSeed.y * 30.0) * 0.18;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize * (0.6 + aSeed.z * 0.8) * projectionMatrix[1][1] * uHalfH / -mv.z;
    float flicker = 0.75 + 0.25 * sin(uTime * (6.0 + aSeed.x * 6.0) + aSeed.y * 40.0);
    vAlpha = smoothstep(0.0, 0.12, life) * (1.0 - smoothstep(0.55, 1.0, life)) * flicker;
  }`;
const AREA_FRAG = `
  uniform vec3 uColor;
  varying float vAlpha;
  void main() {
    float d = length(gl_PointCoord - 0.5);
    float a = 1.0 - smoothstep(0.0, 0.5, d);
    gl_FragColor = vec4(uColor * (1.0 + a), a * a * vAlpha);
  }`;

const AreaParticleField: React.FC<{ area: NonNullable<AtmosphereSettings['areaParticles']> }> = ({ area }) => {
  const { gl } = useThree();
  const count = Math.max(0, Math.min(area.count | 0, 400));
  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const seeds = new Float32Array(count * 4);
    for (let i = 0; i < seeds.length; i++) seeds[i] = Math.random();
    g.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 4));
    // Positions come from the shader; a dummy attribute keeps three happy.
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
    return g;
  }, [count]);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: AREA_VERT, fragmentShader: AREA_FRAG,
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    uniforms: {
      uTime: { value: 0 }, uSize: { value: area.type === 'fireflies' ? 0.1 : 0.07 }, uHalfH: { value: 400 },
      uRise: { value: area.type === 'fireflies' ? 0.15 : 0.45 },
      uMin: { value: new THREE.Vector3(...area.min) }, uMax: { value: new THREE.Vector3(...area.max) },
      uColor: { value: new THREE.Color(area.color) },
    },
  }), [area.type, area.color, area.min[0], area.min[1], area.min[2], area.max[0], area.max[1], area.max[2]]);
  useEffect(() => () => { geom.dispose(); }, [geom]);
  useEffect(() => () => { mat.dispose(); }, [mat]);
  const _size = useMemo(() => new THREE.Vector2(), []);
  useFrame((state) => {
    mat.uniforms.uTime.value = state.clock.elapsedTime;
    const layer = gl.xr.isPresenting ? gl.xr.getSession()?.renderState.baseLayer : null;
    mat.uniforms.uHalfH.value = (layer ? layer.framebufferHeight : gl.getDrawingBufferSize(_size).y) / 2;
  });
  return <points geometry={geom} material={mat} frustumCulled={false} renderOrder={2} raycast={() => null} />;
};

// ---- Main Atmosphere Component ----
const AtmosphereScene: React.FC = () => {
  const atmosphere = useWorldStore((s) => s.atmosphere);
  const isPresenting = useXR((s) => !!s.session);
  const vrBuildMode = useWorldStore((s) => s.vrBuildMode);
  // Lite rendition (phones): bloom stays unless the world turns it off, but the
  // composer drops from 8x to 2x MSAA, its main cost on a phone GPU.
  const liteActive = useLiteStore((s) => s.active);
  const liteBloom = useLiteStore((s) => s.settings?.bloom ?? true);
  const bloomOn = atmosphere.bloom && (!liteActive || liteBloom);

  // Defer heavy visuals (particles, fog planes, bloom) for 3 seconds after mount
  // to avoid lag/screen-door effect on initial world entry
  const [sceneReady, setSceneReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSceneReady(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Show fog planes when fog density is high (Roblox-style layered fog cheat)
  const showFogPlanes = atmosphere.fog === 'exponential' && atmosphere.fogDensity > 0.04;

  return (
    <>
      <FogManager
        fog={atmosphere.fog}
        color={atmosphere.fogColor}
        near={atmosphere.fogNear}
        far={atmosphere.fogFar}
        density={atmosphere.fogDensity}
      />

      {/* Layered fog planes for heavy fog (cheaper than particles, looks better) — skip in VR build mode, deferred until scene ready */}
      {sceneReady && showFogPlanes && !vrBuildMode && (
        <FogPlanes color={atmosphere.fogColor} opacity={Math.min(atmosphere.fogDensity * 3, 0.3)} />
      )}

      {/* Particles — skip entirely in VR build mode for GPU headroom, deferred until scene ready */}
      {sceneReady && atmosphere.particles !== 'none' && !vrBuildMode && (
        <ParticleSystem
          type={atmosphere.particles}
          count={atmosphere.particleCount}
          color={atmosphere.particleColor}
        />
      )}

      {/* Area particles are cheap (GPU-only), so they stay on in VR build mode too. */}
      {sceneReady && atmosphere.areaParticles && atmosphere.areaParticles.count > 0 && (
        <AreaParticleField area={atmosphere.areaParticles} />
      )}

      {/* Lighting overrides from atmosphere */}
      <ambientLight intensity={atmosphere.ambientIntensity} />
      <directionalLight
        position={[50, 50, 25]}
        intensity={atmosphere.sunIntensity}
        color={atmosphere.sunColor}
        castShadow={!!atmosphere.sunShadows && !vrBuildMode}
        key={`sun-${atmosphere.shadowRadius ?? 60}`}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-(atmosphere.shadowRadius ?? 60)}
        shadow-camera-right={atmosphere.shadowRadius ?? 60}
        shadow-camera-top={atmosphere.shadowRadius ?? 60}
        shadow-camera-bottom={-(atmosphere.shadowRadius ?? 60)}
        shadow-camera-near={1}
        shadow-camera-far={200}
        shadow-bias={-0.0004}
      />

      {/* Bloom post-processing (lazy-loaded) — disabled in VR as EffectComposer conflicts with WebXR stereo rendering, deferred until scene ready */}
      {sceneReady && bloomOn && !isPresenting && (
        <Suspense fallback={null}>
          <EffectComposer multisampling={liteActive ? 2 : 8}>
            <Bloom
              intensity={atmosphere.bloomIntensity}
              luminanceThreshold={atmosphere.bloomThreshold}
              luminanceSmoothing={0.9}
              mipmapBlur
            />
          </EffectComposer>
        </Suspense>
      )}
    </>
  );
};

export default AtmosphereScene;
